import { Fragment, useMemo, useState } from 'react'
import { Box, CircularProgress, Divider, Typography, styled } from '@mui/material'

import { IProductConfiguration } from '@/types'
import { DataTable, Order } from '@/components/UI/DataTable'
import { ErrorPage } from '@/pages/Error'
import SearchLogo from '@/assets/search.svg'
import { DEFAULT_SORT_BY, productColumns } from './Columns'
import { ProductDetail } from './ProductDetail'
import { useProductConfigurations } from './useProductConfigurations'

const isMissing = (value: unknown) => value === null || value === undefined || value === ''

// A product the service sent without a name still has to be visible, so those
// rows collect under one section rather than being dropped.
const UNNAMED = 'Unnamed product'

// Latest version first: support is nearly always looking at what is live now,
// and the older versions are there for context beneath it.
const DEFAULT_SORT_ORDER: Order = 'desc'

// Numbers compare numerically; everything else as text, case-insensitively and
// with embedded digits handled naturally ("v2" before "v10").
const compare = (a: unknown, b: unknown): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export const ProductList: React.FC = () => {
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState<Order>(DEFAULT_SORT_ORDER)

  const { data: products, isLoading, isError } = useProductConfigurations()

  // One section per product name: the catalogue carries a row per version, so
  // the name repeats down the table and reads as noise. It becomes the section
  // heading instead — the same treatment eligibility rule categories get — and
  // the versions of one product sit together beneath it.
  //
  // Sections follow the order the names first appear in the payload. Within a
  // section the rows are sorted, newest version first by default; the column
  // headers re-sort inside every section rather than across the whole table.
  //
  // The catalogue is small and fetched whole, so this stays client side. Move it
  // server side if the endpoint ever paginates.
  const groupedProducts = useMemo(() => {
    const groups = new Map<string, IProductConfiguration[]>()

    for (const product of products ?? []) {
      const name = product.name?.trim() ? product.name : UNNAMED
      const existing = groups.get(name)
      if (existing) {
        existing.push(product)
      } else {
        groups.set(name, [product])
      }
    }

    return [...groups.entries()].map(([name, versions]) => [
      name,
      [...versions].sort((rowA, rowB) => {
        const a = rowA[sortBy]
        const b = rowB[sortBy]

        // Products the service gave no value for sink to the bottom in both
        // directions, rather than flooding the top when reversed.
        if (isMissing(a) || isMissing(b)) {
          if (isMissing(a) && isMissing(b)) return 0
          return isMissing(a) ? 1 : -1
        }

        const result = compare(a, b)
        if (result !== 0) return sortOrder === 'asc' ? result : -result

        // Ties resolve by version so the order is stable and predictable.
        return sortBy === 'version' ? 0 : -compare(rowA.version, rowB.version)
      }),
    ]) as [string, IProductConfiguration[]][]
  }, [products, sortBy, sortOrder])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Products'}
        message={
          "We couldn't retrieve the product configurations at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        }
      />
    )
  }

  if (!products || products.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Products Found</Title>
        <Message>There are no product configurations to show.</Message>
      </EmptyContainer>
    )
  }

  return (
    <Box>
      <Typography variant="h6" pb={2}>
        Product Configurations
      </Typography>

      {groupedProducts.map(([name, versions], index) => (
        <Fragment key={name}>
          {index > 0 && <StyledDivider />}
          <Box py={1}>
            <ProductTitle>{name}</ProductTitle>
            <DataTable<IProductConfiguration>
              rows={versions}
              columns={productColumns}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={(property, order) => {
                setSortBy(String(property))
                setSortOrder(order)
              }}
              // Expanding a row is the only thing clicking it does: there is no
              // per-product page, and the rules live on the payload already.
              renderNestedContent={product => <ProductDetail product={product} />}
            />
          </Box>
        </Fragment>
      ))}
    </Box>
  )
}

const StyledDivider = styled(Divider)(() => ({
  borderColor: '#EAECF0',
  padding: '10px 0',
}))

const ProductTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Inter',
  fontWeight: 600,
  color: '#101828',
  marginLeft: '10px',
  marginBottom: '8px',
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
}))
